import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, useWindowDimensions, TouchableOpacity } from 'react-native';
import { Helmet } from 'react-helmet-async';
import { useAppTheme } from '../../utils/useAppTheme';
import {
  getFactorization,
  getDivisorsList,
  getDivisorCount,
  getDivisorsSum,
  getNumberTypeInfo,
  getOrdinalSuffix,
  nthPrime,
  nthComposite,
  nthFibonacci,
  nthTriangular,
  nthSquare,
  nthCube,
  nthTetrahedral,
  nthSquarePyramidal,
  nthStar,
  nthPentagonal,
  parseBase
} from '../../utils/numberProperties';
import { saveToResearch } from '../../utils/researchStorage';
import WebNumberMeaning from '../components/WebNumberMeaning';

export default function NumberPropertiesPage() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [inputText, setInputText] = useState('');
  const [num, setNum] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveMessageType, setSaveMessageType] = useState('success');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const numParam = urlParams.get('num');
      if (numParam) {
        setInputText(numParam);
      } else {
        setInputText('33');
      }
    }
  }, []);

  useEffect(() => {
    const val = parseInt(inputText, 10);
    if (!isNaN(val) && val > 0) {
      setNum(val);
    } else {
      setNum(null);
    }
  }, [inputText]);

  const handleSaveToResearch = async () => {
    if (!num) return;
    try {
      const numStr = num.toString();
      const numType = getNumberTypeInfo(num);
      const note = `[NUMBER] Factorization: ${getFactorization(num)} | ${numType.count}${getOrdinalSuffix(numType.count)} ${numType.type}`;
      
      const dummyResults = [{ name: 'Number Properties', category: 'Properties', totalValue: num }];
      
      await saveToResearch(numStr, {}, dummyResults, note, ['number']);
      setSaveMessageType('success');
      setSaveMessage('Number saved to Research!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessageType('error');
      setSaveMessage(error.message);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const renderContent = () => {
    if (!num) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>Enter a valid positive integer to see properties</Text>
        </View>
      );
    }

    const numType = getNumberTypeInfo(num);

    return (
      <View style={styles.resultsContainer}>
        {/* Top Section - Merged Header & Divisors */}
        <View style={[styles.topSectionRow, isMobile && styles.colMobile]}>
          
          {/* Left Column: Number Identity */}
          <View style={[styles.topLeftColumn, isMobile && styles.colMobileCenter]}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Text style={styles.bigNumber}>{num}</Text>
                <WebNumberMeaning number={num} />
              </View>
            <Text style={styles.factorization}>{getFactorization(num)}</Text>
            <Text style={styles.numberTypeBadge}>
              <Text style={styles.highlight}>{numType.count}{getOrdinalSuffix(numType.count)}</Text> {numType.type}
            </Text>
          </View>

          {/* Right Column: Divisors */}
          <View style={styles.topRightColumn}>
            <Text style={[styles.sectionTitle, { marginBottom: 10, textAlign: 'left' }]}>DIVISORS</Text>
            <View style={styles.divisorsGridRow}>
              <View style={[styles.card, styles.flex1, { marginRight: 10 }]}>
                <Text style={styles.label}>Count:</Text>
                <Text style={styles.value}>{getDivisorCount(num)}</Text>
              </View>
              <View style={[styles.card, styles.flex1]}>
                <Text style={styles.label}>Sum:</Text>
                <Text style={styles.value}>{getDivisorsSum(num)}</Text>
              </View>
            </View>
            <View style={[styles.card, { marginTop: 10 }]}>
              <Text style={styles.label}>List:</Text>
              <Text style={styles.value}>{getDivisorsList(num)}</Text>
            </View>
          </View>
        </View>

        {/* Properties Grid Section */}
        <Text style={[styles.sectionTitle, { marginTop: 25, marginBottom: 10 }]}>THE {num}{getOrdinalSuffix(num).toUpperCase()} IN SEQUENCES</Text>
        <View style={styles.propertiesGrid}>
          <PropertyItem styles={styles} label="PRIME #:" value={nthPrime(num)} isMobile={isMobile} highlight={true} />
          <PropertyItem styles={styles} label="COMPOSITE #:" value={nthComposite(num)} isMobile={isMobile} />
          <PropertyItem styles={styles} label="FIBONACCI #:" value={nthFibonacci(num)} isMobile={isMobile} />
          <PropertyItem styles={styles} label="TRIANGULAR #:" value={nthTriangular(num)} isMobile={isMobile} />
          <PropertyItem styles={styles} label="SQUARE #:" value={nthSquare(num)} isMobile={isMobile} />
          <PropertyItem styles={styles} label="CUBE #:" value={nthCube(num)} isMobile={isMobile} />
          <PropertyItem styles={styles} label="TETRAHEDRAL #:" value={nthTetrahedral(num)} isMobile={isMobile} />
          <PropertyItem styles={styles} label="SQUARE PYRAMIDAL #:" value={nthSquarePyramidal(num)} isMobile={isMobile} />
          <PropertyItem styles={styles} label="STAR #:" value={nthStar(num)} isMobile={isMobile} />
          <PropertyItem styles={styles} label="PENTAGONAL #:" value={nthPentagonal(num)} isMobile={isMobile} />
        </View>

        {/* Conversions Table Section */}
        <Text style={[styles.sectionTitle, { marginTop: 15 }]}>CONVERSIONS</Text>
        <View style={styles.tableContainer}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.tableCellLeft, styles.tableHeaderText]}>FROM:</Text>
            <Text style={[styles.tableCell, styles.tableCellCenter, styles.tableHeaderText]}>NUMERAL SYSTEM:</Text>
            <Text style={[styles.tableCell, styles.tableCellRight, styles.tableHeaderText]}>TO:</Text>
          </View>
          <ConversionRow styles={styles} from={parseBase(num, 8)} system="OCTAL" to={num.toString(8)} />
          <ConversionRow styles={styles} from={parseBase(num, 12)} system="DUODECIMAL" to={num.toString(12)} />
          <ConversionRow styles={styles} from={parseBase(num, 16)} system="HEXADECIMAL" to={num.toString(16)} />
          <ConversionRow styles={styles} from={parseBase(num, 2)} system="BINARY" to={num.toString(2)} isLast />
        </View>
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <Helmet>
        <title>Number Properties Calculator - Gematria Calculator</title>
        <meta name="description" content="Calculate divisors, factorization, numerical sequences, and base conversions for any number." />
      </Helmet>

      <View style={styles.paper}>
        <Text style={styles.pageTitle}>Number Properties Calculator</Text>
        <View style={styles.divider} />
        <Text style={styles.instructions}>Enter a number below to calculate its divisors, factorization, properties, and base conversions.</Text>
        
        <View style={styles.inputContainer}>
          <TextInput
            style={styles.input}
            placeholder="33"
            placeholderTextColor={colors.textSecondary}
            value={inputText}
            onChangeText={setInputText}
            keyboardType="numeric"
            maxLength={10}
          />
        </View>

        {renderContent()}

        {num ? (
          <View style={{ marginTop: 20 }}>
            <TouchableOpacity style={styles.saveBtn} onPress={handleSaveToResearch}>
              <Text style={styles.saveBtnText}>💾 Save to Research</Text>
            </TouchableOpacity>
            {saveMessage ? (
              <Text style={[styles.saveMsg, saveMessageType === 'error' && styles.saveMsgError]}>
                {saveMessage}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}

const METRIC_EXPLANATIONS = {
  "PRIME #:": {
    title: "Prime Number",
    description: "A number greater than 1 that has no positive divisors other than 1 and itself.",
    example: "Example: 2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37..."
  },
  "COMPOSITE #:": {
    title: "Composite Number",
    description: "A positive integer that can be formed by multiplying two smaller positive integers.",
    example: "Example: 4, 6, 8, 9, 10, 12, 14, 15, 16, 18, 20, 21..."
  },
  "FIBONACCI #:": {
    title: "Fibonacci Number",
    description: "A member of the infinite Fibonacci sequence where each term is the sum of the two preceding ones.",
    example: "Example: 1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144..."
  },
  "TRIANGULAR #:": {
    title: "Triangular Number",
    description: "Represents a triangle shape composed of dots. Formula: T(n) = n*(n+1)/2.",
    example: "Example: 1, 3, 6, 10, 15, 21, 28, 36, 45, 55, 66, 78..."
  },
  "SQUARE #:": {
    title: "Square Number",
    description: "An integer that is the square of an integer. Formula: S(n) = n².",
    example: "Example: 1, 4, 9, 16, 25, 36, 49, 64, 81, 100, 121..."
  },
  "CUBE #:": {
    title: "Cube Number",
    description: "An integer that is the cube of an integer. Formula: C(n) = n³.",
    example: "Example: 1, 8, 27, 64, 125, 216, 343, 512, 729..."
  },
  "TETRAHEDRAL #:": {
    title: "Tetrahedral Number",
    description: "A figurate number representing a pyramid with a triangular base (tetrahedron). Formula: Te(n) = n*(n+1)*(n+2)/6.",
    example: "Example: 1, 4, 10, 20, 35, 56, 84, 120, 165, 220..."
  },
  "SQUARE PYRAMIDAL #:": {
    title: "Square Pyramidal Number",
    description: "A figurate number representing the number of spheres in a pyramid with a square base. Formula: P(n) = n*(n+1)*(2n+1)/6.",
    example: "Example: 1, 5, 14, 30, 55, 91, 140, 204, 285, 385..."
  },
  "STAR #:": {
    title: "Star Number",
    description: "A centered figurate number that represents a hexagram (six-pointed star). Formula: S(n) = 6n*(n-1)+1.",
    example: "Example: 1, 13, 37, 73, 121, 181, 253, 337, 433, 541..."
  },
  "PENTAGONAL #:": {
    title: "Pentagonal Number",
    description: "A figurate number representing a regular pentagon shape of dots. Formula: P(n) = n*(3n-1)/2.",
    example: "Example: 1, 5, 12, 22, 35, 51, 70, 92, 117, 145, 176..."
  }
};

const PropertyItem = ({ label, value, isMobile, highlight, styles }) => {
  const [isHovered, setIsHovered] = useState(false);
  const info = METRIC_EXPLANATIONS[label];

  return (
    <View 
      style={[
        styles.propItem, 
        isMobile && styles.propItemMobile, 
        highlight && styles.propItemHighlight,
        { position: 'relative', zIndex: isHovered ? 99 : 1 }
      ]}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Text style={[styles.propLabel, highlight && styles.propLabelHighlight]}>{label}</Text>
      <Text style={[styles.propValue, highlight && styles.propValueHighlight]} numberOfLines={1} adjustsFontSizeToFit>{value}</Text>
      
      {isHovered && info && (
        <View style={styles.tooltipContainer} pointerEvents="none">
          <Text style={styles.tooltipTitle}>{info.title}</Text>
          <Text style={styles.tooltipDesc}>{info.description}</Text>
          <Text style={styles.tooltipExample}>{info.example}</Text>
          <View style={styles.tooltipArrow} />
        </View>
      )}
    </View>
  );
};

const NUMERAL_SYSTEM_EXPLANATIONS = {
  "OCTAL": {
    title: "Octal System (Base 8)",
    description: "A base-8 positional numeral system using digits 0-7. Commonly used in computer programming and system permissions.",
    example: "Example: Decimal 27 is represented as 33 in Octal."
  },
  "DUODECIMAL": {
    title: "Duodecimal System (Base 12)",
    description: "A base-12 system using digits 0-9 and letters A & B (or T & E). Base 12 has more factors than base 10 (2, 3, 4, 6), making division cleaner.",
    example: "Example: Decimal 27 is represented as 23 in Duodecimal."
  },
  "HEXADECIMAL": {
    title: "Hexadecimal System (Base 16)",
    description: "A base-16 system using digits 0-9 and letters A-F. Extensively used in computing to represent byte values in a human-friendly format.",
    example: "Example: Decimal 27 is represented as 1B in Hexadecimal."
  },
  "BINARY": {
    title: "Binary System (Base 2)",
    description: "A base-2 numeral system using only 0 and 1. It is the fundamental representation of instructions and data in all modern digital computing.",
    example: "Example: Decimal 27 is represented as 11011 in Binary."
  }
};

const ConversionRow = ({ from, system, to, isLast, styles }) => {
  const [isHovered, setIsHovered] = useState(false);
  const info = NUMERAL_SYSTEM_EXPLANATIONS[system];

  return (
    <View 
      style={[
        styles.tableRow, 
        isLast && styles.tableRowLast, 
        { position: 'relative', zIndex: isHovered ? 99 : 1 }
      ]}
    >
      <Text style={[styles.tableCell, styles.tableCellLeft, styles.convValue]}>{from}</Text>
      <View 
        style={[styles.tableCell, styles.tableCellCenter, { cursor: 'help' }]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Text style={[styles.convSystem, isHovered && styles.convSystemHovered]}>
          {system}
        </Text>
      </View>
      <Text style={[styles.tableCell, styles.tableCellRight, styles.convValue]}>{to}</Text>
      
      {isHovered && info && (
        <View style={styles.tooltipContainer} pointerEvents="none">
          <Text style={styles.tooltipTitle}>{info.title}</Text>
          <Text style={styles.tooltipDesc}>{info.description}</Text>
          <Text style={styles.tooltipExample}>{info.example}</Text>
          <View style={styles.tooltipArrow} />
        </View>
      )}
    </View>
  );
};

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  contentContainer: {
    padding: 20,
    alignItems: 'center',
  },
  paper: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 20,
    width: '100%',
    maxWidth: 700,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.text,
    textAlign: 'center',
    marginBottom: 10,
  },
  divider: {
    height: 1,
    backgroundColor: colors.divider,
    marginBottom: 15,
  },
  instructions: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: 15,
  },
  inputContainer: {
    alignItems: 'center',
    marginBottom: 15,
  },
  input: {
    width: 250,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingVertical: 12,
    paddingHorizontal: 15,
    fontSize: 24,
    textAlign: 'center',
    backgroundColor: colors.inputBackground,
    color: colors.text,
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: colors.textSecondary,
  },
  resultsContainer: {
    flexDirection: 'column',
    gap: 15,
  },
  topSectionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 20,
    marginBottom: 10,
  },
  topLeftColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topRightColumn: {
    flex: 1.5,
    flexDirection: 'column',
  },
  colMobileCenter: {
    marginBottom: 20,
  },
  divisorsGridRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: 10,
  },
  bigNumber: {
    fontSize: 56,
    fontWeight: 'bold',
    color: colors.primary,
    lineHeight: 60,
    marginBottom: 5,
  },
  factorization: {
    fontSize: 16,
    color: colors.text,
    fontWeight: '500',
    marginBottom: 8,
  },
  numberTypeBadge: {
    backgroundColor: colors.primarySurface,
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 12,
    fontSize: 13,
    color: colors.primary,
    fontWeight: '600',
    overflow: 'hidden',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  divisorsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  colMobile: {
    flexDirection: 'column',
  },
  card: {
    backgroundColor: colors.mutedSurface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  flex1: {
    flex: 1,
  },
  flex2: {
    flex: 2,
  },
  label: {
    fontSize: 12,
    color: colors.textSecondary,
    textTransform: 'uppercase',
    marginBottom: 5,
  },
  value: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.primary,
    textAlign: 'center',
  },
  subText: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '600',
    marginTop: 5,
  },
  highlight: {
    fontWeight: '700',
  },
  propertiesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  propItem: {
    width: 'calc(50% - 5px)',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingVertical: 12,
    paddingHorizontal: 15,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  propItemMobile: {
    width: '100%',
  },
  propItemHighlight: {
    backgroundColor: colors.primarySurface,
    borderWidth: 0,
  },
  propLabel: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  propLabelHighlight: {
    color: colors.primary,
  },
  propValue: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary,
    flexShrink: 1,
    marginLeft: 10,
  },
  propValueHighlight: {
    color: colors.text,
  },
  tableContainer: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    backgroundColor: colors.surface,
    alignSelf: 'center',
    width: '100%',
    maxWidth: 550,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.mutedSurface,
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRowLast: {
    borderBottomWidth: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
  },
  tableCell: {
    flex: 1,
  },
  tableCellLeft: {
    alignItems: 'flex-start',
  },
  tableCellCenter: {
    alignItems: 'center',
  },
  tableCellRight: {
    alignItems: 'flex-end',
  },
  tableHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.text,
  },
  convSystem: {
    fontSize: 13,
    color: colors.text,
    fontWeight: '600',
  },
  convSystemHovered: {
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  convValue: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '700',
  },
  saveBtn: {
    backgroundColor: colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 30,
    borderRadius: 6,
    alignItems: 'center',
    alignSelf: 'center',
    marginBottom: 10,
  },
  saveBtnText: {
    color: colors.onPrimary,
    fontSize: 16,
    fontWeight: '600',
  },
  saveMsg: {
    textAlign: 'center',
    color: colors.success,
    fontSize: 14,
    marginBottom: 15,
  },
  saveMsgError: {
    color: colors.error,
  },
  tooltipContainer: {
    position: 'absolute',
    bottom: '120%',
    left: '2%',
    right: '2%',
    backgroundColor: colors.surfaceElevated,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 12,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
    zIndex: 9999,
  },
  tooltipTitle: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  tooltipDesc: {
    color: colors.text,
    fontSize: 11,
    lineHeight: 15,
    marginBottom: 6,
  },
  tooltipExample: {
    color: colors.textSecondary,
    fontSize: 10,
    fontStyle: 'italic',
  },
  tooltipArrow: {
    position: 'absolute',
    bottom: -6,
    left: '50%',
    marginLeft: -6,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderTopWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: colors.surfaceElevated,
  }
});
